<template>
  <form class="form-invoice-payment" v-on:submit.prevent="onSubmit">
    <the-select
      v-if="isShowCashboxList"
      option-title-key="name"
      option-value-key="id"
      v-bind:optional="false"
      v-bind:options="cashboxList"
      v-bind:value="selectedCashboxId"
      v-on:input="onSelectCashbox"
    />
    <div class="form-invoice-payment__head">
      <span>Сумма возврата:</span>
      <the-rub
        v-bind:show-kop="$options.SHOW_KOP_AUTO"
        v-bind:kop="amountByKop"
        currency="руб"
      />
    </div>
    <div class="form-invoice-payment__inputs">
      <div class="form-invoice-payment__payment-types">
        <label
          class="form-invoice-payment__radio"
          v-for="{ title, value } in refundTypes"
          v-bind:key="value"
        >
          <input
            type="radio"
            name="payment-type"
            v-bind:value="value"
            v-model="selectedRefundType"
          />
          <span>{{ title }}</span>
        </label>
      </div>
    </div>
    <div class="form-invoice-payment__controls">
      <button
        class="form-invoice-payment__buttons__button-cancel"
        type="button"
        v-on:click="dispatchCancel"
      >
        Отмена
      </button>
      <modal-dialog
        persistent
        title="Подтвердите возврат"
        v-model="shownConfirmDialog"
        with-cross
        max-width="400px"
        width="90%">
        <template v-slot:trigger="{ listenerObject }">
          <button
            class="form-invoice-payment__buttons__button-submit"
            type="button"
            v-on="listenerObject"
            v-bind:disabled="isDisabledRefundButton"
          >
            Возврат
          </button>
        </template>
        <div class="form-invoice-payment__confirm-dialog">
          Возврат на сумму: <b>
            <the-rub
              v-bind:show-kop="$options.SHOW_KOP_AUTO"
              v-bind:kop="finalAmount"
              currency="руб." />
          </b>
          <br>
          <div class="form-invoice-payment__confirm-dialog__buttons">
            <button
              class="form-invoice-payment__confirm-dialog__button-cancel"
              type="button"
              v-on:click="shownConfirmDialog = false"
            >
              Отмена
            </button>
            <button
              class="form-invoice-payment__confirm-dialog__button-submit"
              type="button"
              v-bind:disabled="isAwaitRequest"
              v-on:click="onSubmit"
            >
              Подтвердить
            </button>
          </div>
        </div>
      </modal-dialog>
    </div>
  </form>
</template>

<script>
import { PaymentType, RefundRequest } from '@/storage/gateways/InvoiceGateway';
import ModalDialog from '@/components/ModalDialog.vue';
import TheRub, { SHOW_KOP_AUTO } from '@/components/TheRub.vue';
import TheSelect from '@/components/TheSelect.vue';

/**
 * @description Название события закрытия формы.
 * @type {string}
 */
const NAME_EVENT_CANCEL = 'cancel';

/**
 * @description Названия события отправки формы.
 * @type {string}
 */
const NAME_EVENT_SUBMIT = 'submit';

/**
 * @description Форма возврата.
 */
export default {
  components: { ModalDialog, TheRub, TheSelect },
  SHOW_KOP_AUTO,
  computed: {
    /**
     * @description Итогова сумма возврата.
     * @type {number}
     */
    finalAmount() {
      return this.amountByKop;
    },
    /**
     * @description Деактивировать кнопку возврата.
     * @type {boolean}
     */
    isDisabledRefundButton() {
      return !this.selectedRefundType || this.amountByKop === 0;
    },
    /**
     * @description Показывать список касс.
     * @type {boolean}
     */
    isShowCashboxList() {
      return this.cashboxList.length > 1;
    },
  },
  /**
   * @description Тип возврата на аванс.
   * @type {string}
   */
  refundByAdvance: 'advance',
  /**
   * @description Тип возврата безналичными.
   * @type {string}
   */
  refundByCashless: 'cashless',
  data() {
    return {
      /**
       * @description Сумма возврата в копейках.
       * @type {number}
       */
      amountByKop: this.refundAmount,
      /**
       * @description Выбранный тип возврата.
       * @type {string}
       */
      selectedRefundType: '',
      /**
       * @description Показывать диалог подтверждения возврата.
       * @type {boolean}
       */
      shownConfirmDialog: false,
    };
  },
  methods: {
    /**
     * @description Отправить событие отмены.
     * @returns {void}
     */
    dispatchCancel() {
      this.$emit(NAME_EVENT_CANCEL);
    },
    /**
     * @description Создать запрос.
     * @returns {RefundRequest}
     */
    makeRequest() {
      const paymentTypes = [];
      paymentTypes.push(new PaymentType(this.amountByKop, this.selectedRefundType));
      return new RefundRequest(
        this.invoiceId,
        this.selectedCashboxId,
        paymentTypes,
        this.selectedPositions.map(({ position }) => position),
      );
    },
    /**
     * @description Обработать выбор кассы.
     * @param {number} id Идентификатор кассы.
     * @returns {void}
     */
    onSelectCashbox(id) {
      const cashboxId = +id;
      const cashbox = this.cashboxList.find(
        (candidate) => candidate.id === cashboxId,
      );
      if (
        this.selectedRefundType === this.$options.refundByAdvance
        || this.selectedRefundType === this.$options.refundByCashless
      ) {
        this.selectedRefundType = '';
      }
      this.$emit('select-cashbox', cashbox);
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const request = this.makeRequest();
      this.$emit(NAME_EVENT_SUBMIT, request);
    },
  },
  name: 'FormInvoiceRefund',
  props: {
    /**
     * @description Список касс.
     */
    cashboxList: { required: true, type: Array },
    /**
     * @description Сумма возврата.
     * @type {number}
     */
    refundAmount: { required: true, type: Number },
    /**
     * @description Включен тип оплаты авансом.
     */
    refundTypes: { required: true, type: Array },
    /**
     * @description Идентификатор счета.
     * @type {number}
     */
    invoiceId: { type: Number, required: true },
    /**
     * @description Ожидание завершения запроса.
     * @type {boolean}
     */
    isAwaitRequest: { type: Boolean, default: false },
    /**
     * @description Идентификатор выбранной кассы.
     */
    selectedCashboxId: { required: true, type: [Object, Number] },
    /**
     * @description Выбранные позиции.
     */
    selectedPositions: { type: Array, default: () => [] },
  },
};
</script>

<style lang="scss" scoped>
@use "@/scss/buttons";
.form-invoice-payment {
  display: flex;
  flex-direction: column;
  &__buttons {
    &__button-cancel {
      @include buttons.btn-transparent(100%, 56px, 1fr);
    }
    &__button-submit {
      @include buttons.btn-blue(100%, 56px, 1fr);
      &:disabled {
        background-color: var(--unfocus-color);
        border: 1px solid var(--unfocus-color);
        color: white;
      }
    }
  }
  &__confirm-dialog {
    &__buttons {
      display: flex;
      flex-flow: row nowrap;
      margin-top: 18px;
      gap: 8px;
    }
    &__button-cancel {
      @include buttons.btn-transparent(100%, 44px, 16px);
    }
    &__button-submit {
      @include buttons.btn-blue(100%, 44px, 16px);
    }
  }
  &__empty-advance {
    color: var(--warning-color);
  }
  &__head {
    align-items: center;
    column-gap: 15px;
    display: grid;
    grid-template-columns: auto 1fr;
    margin-top: 43px;
    row-gap: 15px;
    padding-bottom: 33px;
    border-bottom: solid 1px #c2cfe0;
    &__amount {
      font-size: 24px;
      font-weight: 600;
      line-height: 29px;
      &.small {
        font-size: 20px;
        font-weight: 600;
        line-height: 24px;
      }
    }
  }
  &__payment {
    &-types {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    &-type {
      margin-top: 20px;
      &__title {
        color: var(--reading-color);
        line-height: 24px;
        font-size: 20px;
        font-weight: bold;
      }
    }
  }
  &__switch::v-deep {
    margin-top: 22px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;
    span {
      font-size: 18px;
      line-height: 22px;
      color: var(--reading-color);
    }
    .v-input--selection-controls__input:hover .v-input--selection-controls__ripple:before {
      content: none;
    }
    .v-input--selection-controls__ripple {
      display: none;
    }
    .v-input--switch .v-input--switch__track {
      background: white !important;
      border: 1px solid #c2cfe0;
      border-radius: 15px;
      opacity: 1 !important;
    }
    .v-input--switch .v-input--switch__thumb {
      color: #90a0b7;
    }
    .v-input--selection-controls {
      margin: 0 !important;
      padding: 5px !important;
    }
  }
  &__radio {
    & > input {
      position: absolute;
      z-index: -1;
      opacity: 0;
    }
    & > span {
      display: inline-flex;
      align-items: center;
      user-select: none;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      cursor: pointer;
    }
    & > span::before {
      content: "";
      display: inline-block;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      flex-grow: 0;
      border: 1px solid #90a0b7;
      border-radius: 50%;
      margin-right: 10px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 50% 50%;
    }
    & > input:not(:disabled):not(:checked) + span:hover::before {
      border: 1px solid #0489e0;
    }
    & > input:checked + span::before {
      border: 1px solid #0489e0;
      background-color: #0489e0;
      box-shadow: inset 0 0 0 6px #fff;
    }
  }
  &__controls {
    margin-top: 61px;
    column-gap: 9px;
    display: grid;
    grid-template-columns: repeat(2, 214px);
    justify-content: flex-end;
  }
  &__inputs {
    margin: 22px 0;
  }
  &__mixed {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 15px;
    &__input {
      border-radius: 4px;
      border: 1px solid #c2cfe0;
      outline: 0;
      padding: 5px 10px;
      height: 36px;
      &:focus {
        border-color: var(--action-color);
      }
      &:hover,
      &:focus{
        appearance: none;
        -moz-appearance: textfield;
      }
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
      }
    }
  }
  &__group {
    display: grid;
    grid-template-columns: 156px 1fr 30px;
    align-items: center;
    column-gap: 8px;
  }
}
@media screen and (max-width: 520px) {
  .form-invoice-payment__controls {
    grid-template-columns: 1fr 1fr;
    justify-content: auto;
  }
}
@media screen and (max-width: 480px) {
  @media screen and (max-width: 480px) {
    .form-invoice-payment {
      &__controls {
        grid-template-columns: 1fr;
        row-gap: 10px;
        margin-top: 20px;
      }

      &__group {
        display: grid;
        grid-template-columns: 1fr 30px;
        align-items: center;
        column-gap: 8px;
        position: relative;
      }

      &__label {
        color: var(--unfocus-color);
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        margin: 0 0 2px 0;
        grid-column: 1/3;
      }
      &__confirm-dialog {
        &__buttons {
          flex-wrap: wrap;
        }
      }
    }
  }
}
</style>
